import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import MethodPickerModal from '../components/qpgeneration/MethodPickerModal';
import OtpDialog from '../components/qpgeneration/OtpDialog';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
    minHeight: 38,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

/**
 * Status badge classes — Pending / Draft / Submitted / Approved / etc.
 */
function StatusBadge({ paper }) {
  if (!paper) {
    return <span className="qpg-badge qpg-badge-pending">Not Started</span>;
  }
  const { status } = paper;
  const cls = {
    DRAFT: 'qpg-badge-draft',
    SUBMITTED: 'qpg-badge-submitted',
    UNDER_REVISION: 'qpg-badge-revision',
    APPROVED: 'qpg-badge-approved',
    REJECTED: 'qpg-badge-rejected',
  }[status] || 'qpg-badge-pending';
  return <span className={`qpg-badge ${cls}`}>{status.replace('_', ' ')}</span>;
}

function MethodLabel({ method }) {
  if (!method) return <span className="qpg-method-muted">—</span>;
  const label = {
    DIRECT_UPLOAD: 'File Upload',
    MANUAL_GENERATION: 'Manual',
    AUTO_GENERATION: 'Auto-Generate',
  }[method] || method;
  return <span className="qpg-method-chip">{label}</span>;
}

function QPGeneration() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [methodFilter, setMethodFilter] = useState(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRow, setPickerRow] = useState(null);
  const [viewOtpOpen, setViewOtpOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [methods, setMethods] = useState({
    allowDirectUpload: true,
    allowManualGeneration: true,
    allowAutoGeneration: true,
  });
  const [templates, setTemplates] = useState([]);

  // -------------------- Loaders --------------------

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.get('/qp-generation/sessions');
      if (res.data.success) {
        const opts = (res.data.data || []).map((s) => ({
          value: s.AcademicSessionID,
          label: `${s.YearName} / ${s.SessionName}`,
        }));
        setSessions(opts);
        // Auto-select the newest
        if (!selectedSession && opts.length > 0) setSelectedSession(opts[0]);
      }
    } catch {
      toast.error('Failed to load sessions');
    }
  }, [selectedSession]);

  const loadMethodsAndTemplates = useCallback(async () => {
    try {
      const [m, t] = await Promise.all([
        api.get('/qp-generation/qp-methods'),
        api.get('/qp-generation/templates'),
      ]);
      if (m.data.success) setMethods(m.data.data);
      if (t.data.success) setTemplates(t.data.data || []);
    } catch {
      // Non-fatal — modal will still open with defaults
    }
  }, []);

  const loadRows = useCallback(async () => {
    if (!selectedSession) return;
    try {
      setLoading(true);
      const res = await api.get(`/qp-generation/my-papers?sessionId=${selectedSession.value}`);
      if (res.data.success) setRows(res.data.data || []);
    } catch {
      toast.error('Failed to load papers');
    } finally {
      setLoading(false);
    }
  }, [selectedSession]);

  useEffect(() => { loadSessions(); loadMethodsAndTemplates(); }, [loadSessions, loadMethodsAndTemplates]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // -------------------- Filtering --------------------

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const hay = `${r.courseCode} ${r.courseName} ${r.examTypeName} ${r.setName} ${r.setter?.name || ''} ${r.moderator?.name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter) {
        const st = r.paper?.status || 'NOT_STARTED';
        if (st !== statusFilter.value) return false;
      }
      if (methodFilter) {
        const m = r.paper?.generationMethod || 'NONE';
        if (m !== methodFilter.value) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, methodFilter]);

  const counts = useMemo(() => {
    const c = { total: rows.length, notStarted: 0, draft: 0, submitted: 0, approved: 0, rejected: 0, revision: 0 };
    for (const r of rows) {
      const s = r.paper?.status || 'NOT_STARTED';
      if (s === 'NOT_STARTED') c.notStarted++;
      else if (s === 'DRAFT') c.draft++;
      else if (s === 'SUBMITTED') c.submitted++;
      else if (s === 'APPROVED') c.approved++;
      else if (s === 'REJECTED') c.rejected++;
      else if (s === 'UNDER_REVISION') c.revision++;
    }
    return c;
  }, [rows]);

  // -------------------- Actions --------------------

  const openPicker = (row) => {
    setPickerRow(row);
    setPickerOpen(true);
  };

  const handleStartPaper = async ({ generationMethod, templateId }) => {
    if (!pickerRow) return;
    try {
      const res = await api.post('/qp-generation/papers', {
        assignmentId: pickerRow.assignmentId,
        generationMethod,
        templateId: templateId || null,
      });
      if (res.data.success) {
        toast.success('Paper started');
        setPickerOpen(false);
        setPickerRow(null);
        // Navigate directly into the editor so the user can start authoring.
        navigate(`/qp-generation/edit/${res.data.data.paperId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start paper');
    }
  };

  const openView = (row) => {
    setViewRow(row);
    setViewOtpOpen(true);
  };

  const handleRequestViewOtp = async () => {
    const res = await api.post(`/qp-generation/papers/${viewRow.paper.paperId}/request-view-otp`);
    return res.data.data || {};
  };

  const handleVerifyView = async (otp) => {
    await api.post(`/qp-generation/papers/${viewRow.paper.paperId}/view`, { otp });
  };

  const handleViewReady = () => {
    setViewOtpOpen(false);
    navigate(`/qp-generation/edit/${viewRow.paper.paperId}`);
  };

  // Row-action label depends on role + current status.
  // Paper editors themselves are a session-2 item — for now we surface the
  // primary action + an informational "Open" link that just refreshes the row.
  const renderRowActions = (row) => {
    const { paper, myRole } = row;

    // No paper yet
    if (!paper) {
      if (myRole === 'Setter' || myRole === 'Admin') {
        return (
          <button className="btn btn-primary btn-sm" onClick={() => openPicker(row)}>
            Start Paper
          </button>
        );
      }
      return <span className="qpg-muted">Awaiting setter</span>;
    }

    // Paper exists — show a status-aware action set.
    // (Editor screens land in the next build; this page already restores visibility.)
    const actions = [];

    if (paper.status === 'DRAFT' || paper.status === 'UNDER_REVISION') {
      if (myRole === 'Setter' || myRole === 'Admin') {
        actions.push(
          <button
            key="edit"
            className="btn btn-primary btn-sm"
            onClick={() => navigate(`/qp-generation/edit/${paper.paperId}`)}
          >
            {paper.status === 'UNDER_REVISION' ? 'Revise' : 'Edit'}
          </button>
        );
      } else if (myRole === 'Moderator') {
        actions.push(
          <span key="wait" className="qpg-muted">Awaiting setter</span>
        );
      }
    }
    if (paper.status === 'SUBMITTED' || paper.status === 'APPROVED' || paper.status === 'REJECTED') {
      actions.push(
        <button
          key="view"
          className="btn btn-outline btn-sm"
          onClick={() => openView(row)}
        >
          View (OTP)
        </button>
      );
    }
    if (actions.length === 0) actions.push(<span key="none" className="qpg-muted">—</span>);
    return <div className="qpg-row-actions">{actions}</div>;
  };

  // -------------------- Render --------------------

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Question Paper Generation</h2>
          <p className="page-subtitle">
            {user?.role === 'InstituteAdmin' || user?.role === 'SuperAdmin'
              ? 'All paper-set assignments for the selected academic session.'
              : 'Papers assigned to you as Setter or Moderator.'}
          </p>
        </div>
      </div>

      {/* Top controls */}
      <div className="qpg-controls">
        <div className="qpg-control">
          <label>Academic Session</label>
          <Select
            options={sessions}
            value={selectedSession}
            onChange={setSelectedSession}
            placeholder="Select session..."
            styles={selectStyles}
            isClearable={false}
          />
        </div>
        <div className="qpg-control">
          <label>Status</label>
          <Select
            options={[
              { value: 'NOT_STARTED', label: 'Not Started' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'SUBMITTED', label: 'Submitted' },
              { value: 'UNDER_REVISION', label: 'Under Revision' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All statuses"
            styles={selectStyles}
            isClearable
          />
        </div>
        <div className="qpg-control">
          <label>Method</label>
          <Select
            options={[
              { value: 'NONE', label: 'Not chosen' },
              { value: 'DIRECT_UPLOAD', label: 'File Upload' },
              { value: 'MANUAL_GENERATION', label: 'Manual' },
              { value: 'AUTO_GENERATION', label: 'Auto-Generate' },
            ]}
            value={methodFilter}
            onChange={setMethodFilter}
            placeholder="All methods"
            styles={selectStyles}
            isClearable
          />
        </div>
        <div className="qpg-control qpg-control-grow">
          <label>Search</label>
          <input
            type="text"
            className="form-control"
            placeholder="Course code / name, setter, moderator..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Stat strip */}
      <div className="qpg-stats">
        <div className="qpg-stat"><span className="qpg-stat-num">{counts.total}</span><span className="qpg-stat-lbl">Total</span></div>
        <div className="qpg-stat qpg-stat-pending"><span className="qpg-stat-num">{counts.notStarted}</span><span className="qpg-stat-lbl">Not Started</span></div>
        <div className="qpg-stat qpg-stat-draft"><span className="qpg-stat-num">{counts.draft}</span><span className="qpg-stat-lbl">Draft</span></div>
        <div className="qpg-stat qpg-stat-submitted"><span className="qpg-stat-num">{counts.submitted}</span><span className="qpg-stat-lbl">Submitted</span></div>
        <div className="qpg-stat qpg-stat-revision"><span className="qpg-stat-num">{counts.revision}</span><span className="qpg-stat-lbl">Under Revision</span></div>
        <div className="qpg-stat qpg-stat-approved"><span className="qpg-stat-num">{counts.approved}</span><span className="qpg-stat-lbl">Approved</span></div>
        <div className="qpg-stat qpg-stat-rejected"><span className="qpg-stat-num">{counts.rejected}</span><span className="qpg-stat-lbl">Rejected</span></div>
      </div>

      {/* Grid */}
      <div className="qpg-grid-wrap">
        {loading ? (
          <div className="qpg-empty">Loading...</div>
        ) : filteredRows.length === 0 ? (
          <div className="qpg-empty">
            {rows.length === 0
              ? 'No paper-set assignments for the selected session.'
              : 'No rows match the current filters.'}
          </div>
        ) : (
          <table className="qpg-grid">
            <thead>
              <tr>
                <th>Course</th>
                <th>Exam Type</th>
                <th>Set</th>
                <th>Setter</th>
                <th>Moderator</th>
                <th>Method</th>
                <th>Status</th>
                <th>Your Role</th>
                <th style={{ minWidth: 180 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.assignmentId}>
                  <td>
                    <div className="qpg-course-code">{r.courseCode}</div>
                    <div className="qpg-course-name">{r.courseName}</div>
                  </td>
                  <td>{r.examTypeName}</td>
                  <td><span className="qpg-set-chip">{r.setName}</span></td>
                  <td>
                    <div>{r.setter?.name || '—'}</div>
                    <div className="qpg-email">{r.setter?.email || ''}</div>
                  </td>
                  <td>
                    <div>{r.moderator?.name || '—'}</div>
                    <div className="qpg-email">{r.moderator?.email || ''}</div>
                  </td>
                  <td><MethodLabel method={r.paper?.generationMethod} /></td>
                  <td><StatusBadge paper={r.paper} /></td>
                  <td><span className="qpg-role-chip">{r.myRole}</span></td>
                  <td>{renderRowActions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MethodPickerModal
        isOpen={pickerOpen}
        onClose={() => { setPickerOpen(false); setPickerRow(null); }}
        onConfirm={handleStartPaper}
        methods={methods}
        templates={templates}
        row={pickerRow}
      />

      <OtpDialog
        isOpen={viewOtpOpen}
        title="View Paper"
        description={viewRow
          ? `An OTP will be sent to your email to unlock ${viewRow.courseCode} (${viewRow.examTypeName}, Set ${viewRow.setName}).`
          : ''}
        requestFn={handleRequestViewOtp}
        verifyFn={handleVerifyView}
        onVerified={handleViewReady}
        onClose={() => { setViewOtpOpen(false); setViewRow(null); }}
      />
    </div>
  );
}

export default QPGeneration;
